import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@figma/fpl-components';
import { Icon16Checkmark } from '@figma/fpl-icons';
import fplTokens from '@figma/fpl-tokens/css-vars';
import * as stylex from '@stylexjs/stylex';
import { AuthContext } from '@/contexts/AuthContext';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { SubscriptionType } from '@/types/shared';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { TextLgRg, Typography, typographyStyles } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { color } from '@/colors';
import { PRICING_TABLE_EVENT_NAMES } from '@/consts/analytics.consts';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { I18N_KEYS } from '@/language/keys';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import { DISCOUNT_BADGE_MIN_HEIGHT } from './PricingPackage';
import { maxTierAccentStyles, maxTierButtonStyles } from './pricing.styles';
import type { ApiObject } from '@/types/analytics.types';

const fplTypographyStyles = stylex.create({
  display: stylex.env.mixin.fpl.typography.textDisplay,
});
const enterpriseCardStyles = {
  card: {
    width: '100%',
    maxWidth: '320px',
    minHeight: '588px',
    backgroundColor: fplTokens.color.bgElevated,
    outline: `1px solid ${fplTokens.color.border}`,
    borderRadius: fplTokens.radius.large,
    p: fplTokens.spacer.x5,
    pt: fplTokens.spacer.x6,
  },
  credits: {
    alignItems: 'flex-start',
    minHeight: '42px',
    mb: fplTokens.spacer.x4,
    gap: fplTokens.spacer.x1,
  },
  description: { minHeight: '44px' },
  cta: { mb: fplTokens.spacer.x4 },
};
const horizontalEnterpriseCardStyles = {
  ...enterpriseCardStyles,
  card: {
    ...enterpriseCardStyles.card,
    maxWidth: undefined,
    minHeight: '328px',
    backgroundColor: 'var(--weave-pricing-team-background)',
    pt: fplTokens.spacer.x5,
    display: { xs: 'flex', sm: 'grid' },
    gridTemplateColumns: '1fr 1fr',
    columnGap: '24px',
    pb: '32px',
    '& > [data-enterprise-heading]': { gridColumn: '1 / -1' },
    '& > [data-enterprise-features]': { gridColumn: 2, gridRow: '2 / 5' },
    '& > [data-enterprise-cta]': { gridColumn: 1 },
  },
  credits: {
    ...enterpriseCardStyles.credits,
    color: fplTokens.color.text,
    '--fpl-icon-color': fplTokens.color.icon,
  },
  description: { minHeight: '22px' },
  cta: {
    ...enterpriseCardStyles.cta,
    ...maxTierAccentStyles,
  },
};
interface EnterprisePackageProps {
  /** Fits the existing Enterprise content beside Team in the Max layout. */
  isMaxTierLayout?: boolean;
  isEnterprisePlanCardExperiment?: boolean;
  reserveDiscountSpace?: boolean;
  analyticsContext?: ApiObject;
}

function EnterprisePackage({
  isMaxTierLayout = false,
  isEnterprisePlanCardExperiment = false,
  reserveDiscountSpace = false,
  analyticsContext,
}: EnterprisePackageProps) {
  const { track } = useAnalytics();
  const { currentUser } = useContext(AuthContext);
  const handleContactSales = () => {
    track(
      PRICING_TABLE_EVENT_NAMES.CONTACT_SALES_CLICKED,
      {
        ...analyticsContext,
        context: 'Payments',
        destination: 'enterprise_contact_form',
        source: 'pricing_table',
        placement: isMaxTierLayout
          ? 'enterprise_card_max_tier'
          : isEnterprisePlanCardExperiment
            ? 'enterprise_card'
            : 'enterprise_banner',
        subscriptionType: currentUser?.activeWorkspace?.subscription?.type ?? SubscriptionType.Free,
      },
      TrackTypeEnum.BI,
    );
    window.open(EXTERNAL_LINKS.enterpriseContactForm, '_blank', 'noopener,noreferrer');
  };

  return isEnterprisePlanCardExperiment || isMaxTierLayout ? (
    <EnterpriseCard
      onContactSales={handleContactSales}
      reserveDiscountSpace={reserveDiscountSpace}
      isMaxTierLayout={isMaxTierLayout}
    />
  ) : (
    <EnterpriseBanner onContactSales={handleContactSales} />
  );
}

function EnterpriseCard({
  onContactSales,
  reserveDiscountSpace,
  isMaxTierLayout,
}: {
  onContactSales: () => void;
  reserveDiscountSpace: boolean;
  isMaxTierLayout: boolean;
}) {
  const { t } = useTranslation();
  const cardStyles = isMaxTierLayout ? horizontalEnterpriseCardStyles : enterpriseCardStyles;
  const featureKeys = [
    I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_TEAM_TRAINING,
    I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_PREMIUM_SUPPORT,
    I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_OWN_API_KEYS,
    I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_API_WORKFLOWS,
  ];

  const creditsSection = (
    <Flex sx={cardStyles.credits}>
      <AsteriskIcon style={{ position: 'relative', top: -1 }} />
      <TextLgRg>{t(I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_CUSTOM_CREDITS)}</TextLgRg>
    </Flex>
  );

  return (
    <FlexCol data-testid="enterprise-plan-card" sx={cardStyles.card}>
      <FlexCol data-enterprise-heading="" sx={{ gap: fplTokens.spacer.x2, mb: fplTokens.spacer.x4 }}>
        <Typography component="h2" variant="unstyled" {...stylex.props(typographyStyles.textHeadingLarge)}>
          {t(I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.PLAN_NAME)}
        </Typography>
        <Box sx={cardStyles.description}>
          <Text.div variant="body-large" color="secondary">
            {t(
              isMaxTierLayout
                ? I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.MAX_LAYOUT_DESCRIPTION
                : I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.TITLE,
            )}
          </Text.div>
        </Box>
      </FlexCol>
      <Flex
        sx={{
          alignItems: 'baseline',
          minHeight: '56px',
          mb: fplTokens.spacer.x3,
        }}
      >
        <Typography component="span" variant="unstyled" {...stylex.props(fplTypographyStyles.display)}>
          {t(I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.PRICE)}
        </Typography>
      </Flex>
      {creditsSection}
      {/* Match the discount badge height on the other cards to keep their CTAs aligned. */}
      {reserveDiscountSpace && <Box aria-hidden sx={{ minHeight: DISCOUNT_BADGE_MIN_HEIGHT }} />}
      <Box data-enterprise-cta="" sx={cardStyles.cta}>
        <ButtonContained
          fullWidth
          mode={!isMaxTierLayout ? 'outlined' : undefined}
          sx={isMaxTierLayout ? maxTierButtonStyles : undefined}
          onClick={onContactSales}
        >
          {t(I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.CTA)}
        </ButtonContained>
      </Box>

      {!isMaxTierLayout && (
        <Box sx={{ mb: fplTokens.spacer.x4, width: '100%' }}>
          <AppDivider />
        </Box>
      )}
      <FlexCol data-enterprise-features="" sx={{ gap: fplTokens.spacer.x2 }}>
        <Text.span variant="body-large-strong" color={isMaxTierLayout ? 'default' : undefined}>
          {t(I18N_KEYS.PAYMENTS.PRICING_TABLE.FEATURES_TITLE, {
            planType: t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TEAM_PLAN.NAME),
          })}
        </Text.span>
        {featureKeys.map((featureKey) => (
          <Flex key={featureKey} style={{ alignItems: 'flex-start', gap: 8 }}>
            <Box style={{ display: 'flex', width: 16, height: 16, flex: '0 0 16px', marginTop: 3 }}>
              <Icon16Checkmark />
            </Box>
            <Text.span variant="body-large" color={isMaxTierLayout ? 'default' : undefined}>
              {t(featureKey)}
            </Text.span>
          </Flex>
        ))}
      </FlexCol>
    </FlexCol>
  );
}
function EnterpriseBanner({ onContactSales }: { onContactSales: () => void }) {
  const { t } = useTranslation();
  const featureKeys = [
    I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_CUSTOM_CREDITS,
    I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_TEAM_TRAINING,
    I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_OWN_API_KEYS,
    I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_API_WORKFLOWS,
    I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.FEATURE_PREMIUM_SUPPORT,
  ];
  return (
    <Flex
      sx={{
        width: '100%',
        minHeight: '294px',
        backgroundColor: fplTokens.color.bg,
        borderRadius: fplTokens.radius.large,
        p: 0,
        pl: { xs: fplTokens.spacer.x5, md: '126px' },
        pt: { xs: fplTokens.spacer.x5, md: '42px' },
        pr: { xs: fplTokens.spacer.x5, md: fplTokens.spacer.x0 },
        pb: { xs: fplTokens.spacer.x5, md: fplTokens.spacer.x0 },
        justifyContent: 'flex-start',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <FlexCol
        sx={{
          width: { xs: '100%', md: '280px' },
          ml: 0,
          mt: { xs: 0, md: '41px' },
          '--color-bg-brand': color.Yellow100,
          '--color-bg-brand-hover': color.Yellow64,
          '--color-bg-brand-pressed': color.Yellow40,
          '--color-icon-onbrand': color.Black100,
          '--color-text-onbrand': color.Black100,
        }}
      >
        <Typography
          component="h2"
          variant="unstyled"
          sx={{ mb: fplTokens.spacer.x5 }}
          {...stylex.props(typographyStyles.textHeadingLarge)}
        >
          {t(I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.TITLE)}
        </Typography>
        <ButtonContained fullWidth onClick={onContactSales}>
          {t(I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.CTA)}
        </ButtonContained>
      </FlexCol>
      <FlexCol
        sx={{
          width: { xs: '100%', md: '388px' },
          mr: 0,
          mt: { xs: 4, md: 0 },
          ml: { xs: 0, md: 9 },
        }}
      >
        <Box sx={{ mb: fplTokens.spacer.x3 }}>
          <Text.div variant="body-large" color="secondary">
            {t(I18N_KEYS.PAYMENTS.ENTERPRISE_PACKAGE.SUBTITLE)}
          </Text.div>
        </Box>
        <FlexCol sx={{ gap: fplTokens.spacer.x2 }}>
          {featureKeys.map((featureKey, index) => (
            <FlexCenVer key={index} sx={{ gap: fplTokens.spacer.x2 }}>
              <CheckMarkIcon width={16} height={16} />
              <Text.span variant="body-large" color="secondary">
                {t(featureKey)}
              </Text.span>
            </FlexCenVer>
          ))}
        </FlexCol>
      </FlexCol>
    </Flex>
  );
}

export default EnterprisePackage;
